import React, { useState, useEffect } from "react";
import { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import GlobalContext from "../../context/GlobalContext";
import { MdKeyboardArrowDown, MdOutlineCloudUpload } from "react-icons/md";
import { FaRegQuestionCircle } from "react-icons/fa";
import { useFormik } from "formik";
import * as Yup from "yup";
import useUpload from "../../services/useUpload";
import { ButtonLoader1 } from "../../components/shared/ButtonLoaders";
import { CiSearch } from "react-icons/ci";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import useFiles from "../../services/useFiles";
import { Delete, DeleteIcon, Trash, Download } from "lucide-react";
import { IconButton } from "@mui/material";
import { getAccessToken } from "../../storage/storage";
import axios from "axios";
import ProfileDropdown from "../../components/shared/profileDropdown";
import Navbar from "../../components/shared/Navbar";
import { List, LayoutGrid } from "lucide-react";
import Swal from "sweetalert2";

function Home() {
  const { files, fetchFiles } = useFiles();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [searchQuery, setSearchQuery] = useState("");

  // Refresh files when refreshTrigger changes
  useEffect(() => {
    fetchFiles();
  }, []);

  const refreshList = () => {
    fetchFiles();

    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <>
      <div className="w-full min-h-[100vh]">
        <UploadDocument onUploadSuccess={refreshList} />
        {/* <FileListView /> */}
        <FileList
          refreshList={refreshList}
          files={files}
          viewMode={viewMode}
          setViewMode={setViewMode}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </div>
    </>
  );
}

export default Home;

// function FileListView() {
//   const { File, files_data } = useFiles();
//   console.log(files_data);

// }
const validationSchema = Yup.object().shape({
  files: Yup.array().min(1, "At least one file is required"),
});

function UploadDocument({ onUploadSuccess }) {
  const { userInfo } = useContext(GlobalContext);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [fileError, setFileError] = useState("");
  const { Upload, upload } = useUpload();
  const navigate = useNavigate();
  const [generatingPass, setGeneratingPass] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState("");

  const initialValues = {
    files: [],
  };

  const { values, errors, handleSubmit, touched, setFieldValue, resetForm } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: async (values, { resetForm }) => {
        const formData = new FormData();
        values.files.forEach((file) => {
          formData.append("files", file);
        });
        await Upload(formData);
        if (!upload.loading && upload.urls.length > 0) {
          setSelectedFiles([]);
          resetForm();
          onUploadSuccess();
        }
      },
    });

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const allowedFileTypes = [".pdf", ".jpg", ".png", ".doc", ".zip", ".docx"];

    const invalidFiles = files.filter((file) => {
      const selectedFileType = file.name.toLowerCase();
      return !allowedFileTypes.some((type) => selectedFileType.endsWith(type));
    });

    if (invalidFiles.length > 0) {
      setFileError("Please select only mentioned files.");
      return;
    }

    const newFiles = [...selectedFiles, ...files];
    setSelectedFiles(newFiles);
    setFieldValue("files", newFiles);
    setFileError("");
  };

  const openQrModal = () => {
    const qr = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
      `https://medtag.app/summary/${userInfo?.id}`
    )}&size=200x200`;

    setQrUrl(qr);
    setQrModalOpen(true);
  };

  const downloadQr = () => {
    const link = document.createElement("a");
    link.href = qrUrl;
    link.setAttribute("download", `medtag_${userInfo?.id}_qr.png`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const allowedFileTypes = [".pdf", ".jpg", ".png", ".doc", ".zip", ".docx"];

    const invalidFiles = files.filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      return !allowedFileTypes.includes(`.${fileExtension}`);
    });

    if (invalidFiles.length > 0) {
      setFileError("Please select only mentioned files.");
      return;
    }

    const newFiles = [...selectedFiles, ...files];
    setSelectedFiles(newFiles);
    setFieldValue("files", newFiles);
    setFileError("");
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const formatFileSize = (sizeInBytes) => {
    const sizeInMb = sizeInBytes / (1024 * 1024);
    return sizeInMb.toFixed(2) + " MB";
  };

  const removeFile = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
    setFieldValue("files", updatedFiles);
  };

  const downloadAppleWalletPass = async () => {
    try {
      const token = getAccessToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/generate-pass/${userInfo?.id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      // Create a blob URL from the response
      const url = window.URL.createObjectURL(new Blob([response.data]));

      // Create a temporary <a> element
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `medtag_${userInfo?.id}.pkpass`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Apple Wallet pass:", error);
    }
  };

  const fetchPassUrl = async () => {
    try {
      setGeneratingPass(true);
      const token = getAccessToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/get-pass-url/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const url = res.data.url;
      setDownloadUrl(url);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `medtag_${userInfo?.id}.pkpass`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error generating Apple Wallet Pass:", error);
    } finally {
      setGeneratingPass(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="mt-10 px-20 w-full">
        <h1 className="font-normal text-[32px]">
          Good Morning,{" "}
          <span className="font-extrabold text-primary">
            {" "}
            {userInfo?.first_name} {userInfo?.last_name}
          </span>
        </h1>
        <h1 className="mt-6 font-[600] text-[24px] text-primary text-center">
          Add Documents
        </h1>
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="flex flex-col justify-center items-center mt-3 px-6 py-12 border-2 border-primary border-dashed rounded-[12px]"
        >
          <label htmlFor="file">
            <MdOutlineCloudUpload className="text-[60px] text-primary" />
          </label>
          <p className="text-[#28372B] text-center">
            Drag & Drop or choose files to upload
          </p>
          <p className="text-[#51785E] text-sm">
            Select multiple zip, image, pdf or ms.word files
          </p>
          <input
            type="file"
            id="file"
            name="file"
            multiple
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>
        {fileError && <div className="mt-2 text-red-500">{fileError}</div>}
        {selectedFiles.length > 0 && (
          <div className="justify-start py-2 w-full">
            <p className="mb-2 font-semibold text-[#4E4949]">
              Selected Files ({selectedFiles.length}):
            </p>
            <div className="max-h-40 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center py-1 border-b"
                >
                  <div>
                    <p className="font-normal text-[#4E4949] text-xs">
                      {file.name}
                    </p>
                    <p className="font-normal text-[#4E4949] text-[10px]">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="hover:bg-red-50 px-2 py-1 rounded text-red-500 text-xs"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* <div className="flex items-center my-4">
          <div className="flex-grow border-gray-300 border-t"></div>
          <span className="mx-4 text-gray-500">OR</span>
          <div className="flex-grow border-gray-300 border-t"></div>
        </div> */}
        <div className="flex md:flex-row flex-col md:justify-between items-center md:items-center gap-4 mt-6 w-full">
          {/* Help text */}
          <div className="flex items-center text-sm md:text-base">
            <FaRegQuestionCircle className="mr-2 w-4 md:w-5 h-4 md:h-5 text-[#293728]" />
            <span className="font-semibold text-[#293728]">
              Still need help?
            </span>
          </div>

          {/* Buttons group */}
          <div className="flex gap-2">
            <button className="px-4 py-2 border border-primary rounded-lg text-primary text-sm md:text-base">
              Cancel
            </button>
            <button
              onClick={(e) => handleSubmit(e)}
              className="bg-primary px-4 py-2 border border-primary rounded-lg text-white text-sm md:text-base"
              disabled={upload.loading || selectedFiles.length === 0}
            >
              {upload.loading ? <ButtonLoader1 /> : "Upload"}
            </button>
          </div>
        </div>
      </div>
      <div className="flex md:flex-row flex-col justify-center items-center gap-3 md:gap-5 mt-10 md:mt-4">
        <button
          onClick={openQrModal}
          className="px-4 py-2 border border-primary rounded-[25px] w-[250px] text-primary"
        >
          QR Code
        </button>
        {downloadUrl ? (
          <a
            href={downloadUrl || "#"}
            className="inline-block px-4 py-2 border border-primary rounded-[25px] w-[250px] text-primary text-center"
          >
            Apple Wallet Pass
          </a>
        ) : (
          <button
            onClick={fetchPassUrl}
            className="px-4 py-2 border border-primary rounded-[25px] w-[250px] text-primary flex justify-center items-center"
            disabled={generatingPass}
          >
            {generatingPass ? (
              <span className="loader mr-2 w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin"></span>
            ) : (
              "Generate Apple Wallet Pass"
            )}
          </button>
        )}

        <button
          onClick={() => navigate(`/summary/${userInfo?.id}`)}
          className="flex justify-center items-center gap-2 bg-primary px-6 py-3 border border-primary rounded-full w-[250px] text-white"
        >
          View Summary
          <img
            src="/assets/viewsummary.svg"
            alt="View Summary Icon"
            className="w-5 h-5"
          />
        </button>
      </div>
      {qrModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg relative w-[300px] text-center">
            <button
              className="absolute top-2 right-2 text-gray-500"
              onClick={() => setQrModalOpen(false)}
            >
              ✕
            </button>

            <h2 className="text-lg font-semibold mb-4">Your QR Code</h2>
            {qrUrl && (
              <img
                src={qrUrl}
                alt="QR Code"
                className="mx-auto mb-4 border rounded"
              />
            )}
            <button
              onClick={downloadQr}
              className="bg-primary text-white px-4 py-2 rounded-lg"
            >
              Download QR
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function FileList({
  refreshList,
  files = [],
  viewMode = "list",
  setViewMode,
  searchQuery,
  setSearchQuery,
}) {
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const handleDeleteFile = async (file_id) => {
    try {
      const token = getAccessToken();
      await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/upload/`, {
        data: { document_id: file_id },
        headers: { Authorization: `Bearer ${token}` },
      });
      refreshList();
    } catch (error) {
      console.error("Delete error:", error);

      const msg =
        error.response?.data?.message ||
        "Something went wrong while deleting the file.";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonColor: "#d33",
      });
    }
  };

  const handlePreviewFile = async (file) => {
    try {
      const token = getAccessToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/get-document-download-link/${file.id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const url = response.data?.download_url;
      if (url) {
        setPreviewUrl(url);
        setPreviewOpen(true);
      } else {
        Swal.fire({
          icon: "error",
          title: "Preview Failed",
          text: "The server did not return a valid preview link.",
          confirmButtonColor: "#d33",
        });
      }
    } catch (error) {
      console.error("Preview error:", error);

      const msg =
        error.response?.data?.message ||
        "Unable to fetch the file preview link. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Preview Failed",
        text: msg,
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      const token = getAccessToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/get-document-download-link/${file.id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const downloadUrl = response.data?.download_url;
      if (downloadUrl) {
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        link.remove();

        Swal.fire({
          icon: "success",
          title: "Download Started",
          text: `Your file "${file.filename}" is being downloaded.`,
          confirmButtonColor: "#3085d6",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Download Failed",
          text: "The server did not return a valid download link.",
          confirmButtonColor: "#d33",
        });
      }
    } catch (error) {
      console.error("Download error:", error);

      const msg =
        error.response?.data?.message ||
        "Unable to fetch the file download link. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: msg,
        confirmButtonColor: "#d33",
      });
    }
  };

  return (
    <div className="mt-12 px-12">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="font-semibold text-lg">
            Documents | {files?.length || 0} Files
          </h2>
          <p>Click on a file to preview the file</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 w-[300px]">
        <CiSearch className="top-2.5 left-3 absolute text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Search files..."
          className="p-2 pl-10 border rounded-md w-full"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* View toggle */}
      <div className="flex justify-end mb-4 gap-2">
        <button
          onClick={() => setViewMode("list")}
          className={`p-2 border rounded-md ${
            viewMode === "list"
              ? "bg-primary text-white"
              : "border-primary text-primary"
          }`}
        >
          <List className="w-6 h-6" />
        </button>
        <button
          onClick={() => setViewMode("grid")}
          className={`p-2 border rounded-md ${
            viewMode === "grid"
              ? "bg-primary text-white"
              : "border-primary text-primary"
          }`}
        >
          <LayoutGrid className="w-6 h-6" />
        </button>
      </div>

      {/* Files */}
      <div className="mt-2">
        {files?.length > 0 ? (
          viewMode === "list" ? (
            <>
              {/* Desktop */}
              <div className="hidden md:block">
                <table className="w-full text-left">
                  <thead>
                    <tr>
                      <th className="py-2">File name</th>
                      <th className="py-2">Location (Folder)</th>
                      <th className="py-2">File type</th>
                      <th className="py-2">Modified</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {files
                      .filter((file) =>
                        file.filename
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase())
                      )
                      .map((file, index) => (
                        <tr
                          key={index}
                          className="cursor-pointer hover:bg-green-50"
                          onClick={() => handlePreviewFile(file)}
                        >
                          <td className="py-2 font-semibold text-[#2B2B2B]">
                            {file.filename}
                          </td>
                          <td className="py-2 text-primary">
                            Uploaded Documents
                          </td>
                          <td className="py-2">
                            .{file.filename.split(".").pop()}
                          </td>
                          <td className="py-2">
                            {new Date(file.uploaded_at).toLocaleDateString()}
                          </td>
                          <td
                            className="py-2 flex gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              onClick={() => handleDownloadFile(file)}
                            >
                              <Download />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteFile(file.id)}
                            >
                              <Trash />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile */}
              <div className="md:hidden flex flex-col gap-4">
                {files
                  .filter((file) =>
                    file.filename
                      .toLowerCase()
                      .includes(searchQuery.toLowerCase())
                  )
                  .map((file, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-start shadow-sm px-4 py-3 border rounded-md cursor-pointer hover:bg-gray-50"
                      onClick={() => handlePreviewFile(file)}
                    >
                      <div>
                        <p className="font-semibold text-[#2B2B2B] text-base">
                          {file.filename}
                        </p>
                        <p className="flex items-center gap-2 mt-1 text-[#1D824D] text-sm">
                          <span>Uploaded Documents</span>
                          {file.uploaded_at && (
                            <>
                              <span className="text-gray-400">|</span>
                              <span className="text-gray-700 text-xs">
                                {new Date(file.uploaded_at).toLocaleDateString(
                                  "en-US"
                                )}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                      <div
                        className="flex gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconButton onClick={() => handleDownloadFile(file)}>
                          <Download />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteFile(file.id)}>
                          <Trash />
                        </IconButton>
                      </div>
                    </div>
                  ))}
              </div>
            </>
          ) : (
            // Grid view
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-4">
              {files
                .filter((file) =>
                  file.filename
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase())
                )
                .map((file, index) => (
                  <div
                    key={index}
                    onClick={() => handlePreviewFile(file)}
                    className="cursor-pointer bg-green-100 rounded-lg border border-green-300 flex flex-col justify-between h-48 hover:shadow-md transition"
                  >
                    <div className="flex-grow flex items-center justify-center">
                      <img
                        src="/assets/pdf-shape.svg"
                        alt="file"
                        className="w-10 h-10"
                      />
                    </div>
                    <div className="flex justify-between items-center bg-white rounded-b-lg px-3 py-2 border-t border-green-300">
                      <p className="text-sm font-semibold truncate">
                        {file.filename}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          )
        ) : (
          <div className="py-10 text-gray-500 text-center">
            No files uploaded yet. Upload some files to see them here.
          </div>
        )}
      </div>
      <div className="h-16"></div>

      {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-5xl h-[80vh] rounded-lg shadow-lg overflow-hidden flex flex-col">
            <div className="flex justify-between items-center px-4 py-3 bg-gray-100 border-b">
              <h2 className="font-semibold text-lg">File Preview</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setPreviewOpen(false)}
              >
                ✕
              </button>
            </div>
            <iframe
              src={previewUrl}
              title="File Preview"
              className="flex-1 w-full"
            />
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[350px] text-center relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={() => setErrorModalOpen(false)}
            >
              ✕
            </button>
            <h2 className="text-lg font-semibold text-red-600 mb-3">Error</h2>
            <p className="text-gray-700 mb-5">{errorMessage}</p>
            <button
              className="bg-primary text-white px-4 py-2 rounded-md"
              onClick={() => setErrorModalOpen(false)}
            >
              Ok
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
